import i18n from "../i18n";
import getErrorMessage from "../utils/getErrorMessage";
import ensureSingleExecution from "../middleware/ensureSingleExecution";
import { convertFractionToDecimal } from "../utils/formHelper";

const MealPlanner = (app) => {
  app.get("/meal-planner/meal-plan-review", async ({ req, res, api }) => {
    const { mealPlanId } = req.query || {};
    if (!mealPlanId) {
      res.status = 400;
      res.body = { error: "mealPlanId required" };
      return;
    }
    try {
      const response = await api("meal-planner/meal-plan-review", {
        query: { mealPlanId },
      });
      const data = await response.json();
      res.status = response.ok ? 200 : response.status;
      res.body = data;
    } catch (e) {
      res.status = 500;
      res.body = { error: e?.message || "Failed to fetch review" };
    }
  });

  /**
   * Ingredients of an external recipe stored in a detailed meal plan (Food modal)
   */
  app.get("/meal-planner/recipe-ingredients", async ({ req, res, api }) => {
    const { mealPlanId, ...query } = req.query || {};
    if (!mealPlanId) {
      res.status = 400;
      res.body = { error: "mealPlanId required" };
      return;
    }
    try {
      const response = await api(`meal-plan-recipe-ingredient/${mealPlanId}`, {
        query,
      });
      res.status = 200;
      res.body = await response.json();
    } catch (e) {
      // Keep the API status (e.g. 409 when the plan changed) so the modal can react to it
      res.status = e?.status ?? e?.code ?? 500;
      res.body = { error: e?.message || "Failed to fetch recipe ingredients" };
    }
  });

  app.get("/meal-planner/ai/recipe-category-count", async ({ res, api }) => {
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";

    try {
      const response = await api("meal-planner/ai/recipe-category-count");
      const payload = await response.text();
      res.status = response.status;
      res.body = payload || "{}";
    } catch (e) {
      res.status = 500;
      res.body = JSON.stringify({
        error: e?.message || "Failed to fetch recipe category counts",
      });
    }
  });

  /**
   * And or edit a food
   */
  app.post(
    "/meal-planner/handle-food",
    ensureSingleExecution,
    async ({ req, res, flash, api, assert, locals }) => {
      const { body, query } = req;
      const { foodId } = query;
      let { quantityNumber } = body;
      let { quantityDescription } = body;
      const { name, measurementsValue, unitOfMeasure } = body;

      const { activeUserToRequest } = locals;
      /**
       * If the quantity number is not provided, set it to 1
       */
      if (
        quantityNumber === undefined ||
        quantityNumber === null ||
        quantityNumber === "" ||
        (typeof quantityNumber === "string" && !String(quantityNumber).trim())
      ) {
        quantityNumber = 1;
      }
      /**
       * If the quantity description is not provided, set it to "serving"
       */
      if (!quantityDescription || !String(quantityDescription).trim()) {
        quantityDescription = "serving";
      }

      const validationErrors = [
        {
          value: name,
          message: i18n.t("coaching-portal:mealPlannerActions.provideFoodName"),
        },
        {
          value: measurementsValue,
          message: i18n.t(
            "coaching-portal:mealPlannerActions.provideMeasurementsValue"
          ),
        },
        {
          value: unitOfMeasure,
          message: i18n.t(
            "coaching-portal:mealPlannerActions.provideUnitOfMeasure"
          ),
        },
      ];

      for (const { value, message } of validationErrors) {
        assert(value, 400, message);
      }

      quantityNumber = convertFractionToDecimal(quantityNumber);
      if (Number.isNaN(quantityNumber) || quantityNumber < 0) {
        quantityNumber = 1;
      }

      const bodyToSend = {
        ...body,
        quantityNumber,
        quantityDescription,
        coachId: activeUserToRequest._id,
      };

      try {
        const method = foodId ? "PUT" : "POST";
        const endpoint = foodId ? `foods/${foodId}` : `foods`;
        const message = foodId ? "updated" : "created";
        await api(endpoint, {
          method,
          body: bodyToSend,
        });

        flash(
          "success",
          message === "updated"
            ? i18n.t(
                "coaching-portal:mealPlannerActions.foodUpdatedSuccessfully"
              )
            : i18n.t(
                "coaching-portal:mealPlannerActions.foodCreatedSuccessfully"
              )
        );
        res.refreshWith(
          { "modal[type]": "create-meal-planner-food", foodId: "" },
          { url: "back", hash: "#_" }
        );
      } catch (e) {
        flash("error", e.message);
        res.backDontScroll();
      }
    }
  );
  /**
   * Add / edit meals
   */
  app
    .post(
      "/meal-planner/handle-meal",
      ensureSingleExecution,
      async ({ req, res, flash, api, assert, locals }) => {
        const { body, query } = req;
        const { mealId } = query;
        const { activeUserToRequest } = locals;
        const bodyToSend = {
          ...body,
          coachId: activeUserToRequest._id,
        };

        try {
          const method = mealId ? "PUT" : "POST";
          const endpoint = mealId ? `meals/${mealId}` : `meals`;
          const message = mealId ? "updated" : "created";
          await api(endpoint, {
            method,
            body: bodyToSend,
          });

          flash(
            "success",
            message === "updated"
              ? i18n.t(
                  "coaching-portal:mealPlannerActions.mealUpdatedSuccessfully"
                )
              : i18n.t(
                  "coaching-portal:mealPlannerActions.mealCreatedSuccessfully"
                )
          );
          res.refreshWith(
            { "modal[type]": "create-meal-planner-food", mealId: "" },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", getErrorMessage(e));
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/create-simplified-meal-plan-for-widget",
      async ({ req, res, flash, api }) => {
        const { body, files } = req;
        try {
          const request = await api("create-simplified-meal-plan-for-widget", {
            method: "POST",
            body,
            files,
          });
          const responseData = await request.json();
          const mealPlanId = responseData?.mealPlanId;
          if (!mealPlanId) {
            throw new Error(
              responseData?.message || "Could not create meal plan"
            );
          }
          flash("success", "Meal plan created successfully");
          const widgetId = body.widgetId ? `&widget=${body.widgetId}` : "";
          res.redirect(
            `/coaching-portal/templates/edit-meal-plan?mealPlan=${mealPlanId}${widgetId}`
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/create-meal-plan",
      ensureSingleExecution,
      async ({ req, res, flash, api }) => {
        const { body, query } = req;
        const { proxyAthlete } = query;

        try {
          const method = "POST";
          const endpoint = `meal-plan/?proxyAthlete=${proxyAthlete}`;
          const message = "created";
          const { fromSaveAsTemplate } = body;
          // Extract frontend-only fields before sending to API (these are only for frontend logic)
          const omitFromApi = new Set([
            "fromSaveAsTemplate",
            "dayType",
            "date",
            "optionId",
            "saveAsTemplate",
            "requestId",
          ]);
          const bodyForApi = Object.fromEntries(
            Object.entries(body).filter(([k]) => !omitFromApi.has(k))
          );
          const request = await api(endpoint, {
            method,
            body: bodyForApi,
          });
          const { nutritionPlanId } = body;
          const responseData = await request.json();
          const mealPlanId = responseData?.mealPlanId;
          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.mealPlanCreatedSuccessfully"
            )
          );

          // If creating a template from "save as template" action, just close the modal
          // (user stays on the current meal plan view)
          if (fromSaveAsTemplate) {
            res.refreshWith(
              {
                modal: "",
                dayType: "",
                date: "",
                optionId: "",
                planMode: "",
                saveAsTemplate: "",
              },
              { url: "back", hash: "#_" }
            );
          } else {
            res.refreshWith(
              {
                section: "meal-planner",
                plan: nutritionPlanId,
                mealPlan: mealPlanId,
                modal: "",
              },
              { url: "back", hash: "#_" }
            );
          }
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/update-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body, query } = req;
        const { mealPlanId, proxyAthlete } = query;
        try {
          await api(`meal-plan/${mealPlanId}/?proxyAthlete=${proxyAthlete}`, {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.mealPlanUpdatedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
              mealPlannerDate: "",
              mealPlannerMeal: "",
              templateId: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/handle-simplified-meal-plan-option",
      async ({ req, res, flash, api }) => {
        const { body, query } = req;
        const { mealPlanId } = query;
        try {
          await api(`handle-simplified-meal-plan-option/${mealPlanId}`, {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.mealPlanUpdatedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
              dayType: "",
              optionId: "",
              date: "",
              mealPlanName: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/replace-option-from-template",
      ensureSingleExecution,
      async ({ req, res, flash, api }) => {
        const { body, query } = req;
        const { mealPlanId } = query;
        try {
          await api(`replace-option-from-template/${mealPlanId}`, {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.optionReplacedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
              dayType: "",
              optionId: "",
              date: "",
              planMode: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/replace-day-from-template",
      ensureSingleExecution,
      async ({ req, res, flash, api }) => {
        const { body, query } = req;
        const { mealPlanId } = query;
        try {
          await api(`replace-day-from-template/${mealPlanId}`, {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t("coaching-portal:mealPlannerActions.dayReplacedSuccessfully")
          );
          res.refreshWith(
            {
              "modal[type]": "",
              templateId: "",
              date: "",
              isTemplate: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/copy-to-from-meal-template",
      ensureSingleExecution,
      async ({ req, res, flash, api }) => {
        const { body } = req;

        try {
          await api(`copy-to-from-meal-template`, {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.mealPlanTemplateUpdatedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
              templateId: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/add-week-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body, query } = req;
        const { mealPlanId, proxyAthlete } = query;
        try {
          await api(
            `add-week-meal-plan/${mealPlanId}/?proxyAthlete=${proxyAthlete}`,
            {
              method: "PUT",
              body,
            }
          );

          flash(
            "success",
            i18n.t("coaching-portal:mealPlannerActions.weekAddedSuccessfully")
          );
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post("/meal-planner/create-template", async ({ req, res, flash, api }) => {
      const { body } = req;
      try {
        await api(`meal-plan-template`, {
          method: "POST",
          body,
        });

        flash(
          "success",
          i18n.t(
            "coaching-portal:mealPlannerActions.mealPlanTemplateCreatedSuccessfully"
          )
        );
        res.refreshWith(
          { "modal[type]": "", mealPlannerDate: "", mealPlannerMeal: "" },
          { url: "back", hash: "#_" }
        );
      } catch (e) {
        flash("error", e.message);
        res.backDontScroll();
      }
    })
    .post(
      "/meal-planner/create-new-template",
      ensureSingleExecution,
      async ({ locals, req, res, flash, api }) => {
        const { body } = req;
        const { activeUserToRequest } = locals;
        const updatedBody = { ...body, coachId: activeUserToRequest._id };
        delete updatedBody.requestId;
        try {
          await api(`new-empty-meal-plan-template`, {
            method: "POST",
            body: updatedBody,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.mealPlanTemplateCreatedSuccessfully"
            )
          );
          res.refreshWith({ "modal[type]": "" }, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    /**
     * Add food to a day, reorder custom meals, add custom meals, remove custom meals
     */
    .post(
      "/meal-planner/update-days-meal-plan",
      ensureSingleExecution,
      async ({ req, res, flash, api }) => {
        const { body, query } = req;
        const { mealPlanId, proxyAthlete } = query;
        const { bodyAction } = body;
        const queryParams = `${mealPlanId}/?proxyAthlete=${proxyAthlete}`;
        const endpoint =
          bodyAction === "handle-food"
            ? `handle-meal-to-meal-plan`
            : bodyAction === "handle-day"
              ? "move-duplicate-day-meal-plan"
              : "handle-custom-meal";

        delete body.bodyAction;
        try {
          await api(`${endpoint}/${queryParams}`, {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.mealPlanUpdatedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
              mealPlannerDate: "",
              mealPlannerMeal: "",
              dayType: "",
              optionId: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", getErrorMessage(e));
          res.backDontScroll();
        }
      }
    )
    /**
     * Add foods to a day and also save it as a meal
     */
    .post(
      "/meal-planner/update-days-meal-plan-and-add-meal",
      ensureSingleExecution,
      async ({ req, res, flash, api, locals }) => {
        const { activeUserToRequest } = locals;
        const { body, query } = req;
        const { mealPlanId, proxyAthlete } = query;
        const queryParams = `${mealPlanId}/?proxyAthlete=${proxyAthlete}`;
        const bodyToSend = {
          ...body,
          coachId: activeUserToRequest._id,
        };
        try {
          // The plan is saved first: the new meal copies its Spoonacular recipes (with their
          // ingredient edits) from the saved plan meal, found through mealPlanId
          await api(`handle-meal-to-meal-plan/${queryParams}`, {
            method: "PUT",
            body: bodyToSend,
          });
          await api("meals", {
            method: "POST",
            body: { ...bodyToSend, mealPlanId },
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.mealPlanUpdatedSuccessfully"
            )
          );
          res.refreshWith(
            { "modal[type]": "", mealPlannerDate: "", mealPlannerMeal: "" },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", getErrorMessage(e));
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/delete-meal-plan",
      async ({ req, res, flash, api, locals }) => {
        const { query = {} } = req;
        const {
          mealPlanId,
          proxyAthlete = null,
          nutritionPlanId = null,
        } = query;

        try {
          await api(
            `meal-plan/${mealPlanId}?proxyAthlete=${proxyAthlete}&nutritionPlanId=${nutritionPlanId}`,
            {
              method: "DELETE",
            }
          );

          flash(
            "success",
            i18n.t("coaching-portal:mealPlannerActions.successfullyDeleted")
          );
          if (nutritionPlanId) {
            res.refreshWith(
              {
                section: "",
                plan: "",
                mealPlan: "",
                week: "",
                tabView: "plans",
              },
              { url: "back", hash: "#_" }
            );
          } else {
            const { planMode, page } = query;
            const params = new URLSearchParams({
              owner: "coach",
              tabView: "meal-plans-tab",
            });
            if (planMode) params.set("planMode", planMode);
            if (page) params.set("page", page);
            res.redirect(`/coaching-portal/all-templates?${params.toString()}`);
          }
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/duplicate-meal-plan",
      async ({ req, res, flash, api }) => {
        const { query = {} } = req;
        const {
          mealPlanId,
          proxyAthlete = null,
          nutritionPlanId = null,
          planMode = null,
        } = query;

        try {
          await api(
            `duplicate-meal-plan/${mealPlanId}?proxyAthlete=${proxyAthlete}`,
            {
              method: "POST",
            }
          );

          flash(
            "success",
            i18n.t("coaching-portal:mealPlannerActions.templateDuplicated")
          );
          if (nutritionPlanId) {
            res.refreshWith(
              {
                section: "",
                plan: "",
                mealPlan: "",
                week: "",
                tabView: "plans",
              },
              { url: "back", hash: "#_" }
            );
          } else {
            const planModeParam = planMode ? `&planMode=${planMode}` : "";
            res.redirect(
              `/coaching-portal/all-templates?owner=coach&tabView=meal-plans-tab${planModeParam}`
            );
          }
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/duplicate-week-to-weeks-meal-plan",
      ensureSingleExecution,
      async ({ req, res, flash, api }) => {
        const { query = {}, body } = req;
        const { mealPlanId, proxyAthlete = null } = query;

        try {
          await api(
            `duplicate-week-to-weeks-meal-plan/${mealPlanId}?proxyAthlete=${proxyAthlete}`,
            {
              method: "PUT",
              body,
            }
          );
          flash(
            "success",
            i18n.t("coaching-portal:mealPlannerActions.weekDuplicated")
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/delete-week-meal-plan",
      async ({ req, res, flash, api }) => {
        const { query = {} } = req;
        const { mealPlanId, week } = query;
        try {
          await api(`remove-week-meal-plan/${mealPlanId}?week=${week}`, {
            method: "PUT",
          });

          flash(
            "success",
            i18n.t("coaching-portal:mealPlannerActions.successfullyDeleted")
          );
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/remove-foods-of-meal",
      async ({ req, res, flash, api }) => {
        const { body, query } = req;
        const { mealPlanId, proxyAthlete } = query;
        const queryParams = `${mealPlanId}/?proxyAthlete=${proxyAthlete}`;

        try {
          await api(`remove-foods-of-meal/${queryParams}`, {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.mealPlanUpdatedSuccessfully"
            )
          );
          res.backDontScroll();
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/duplicate-day-meal-plan",
      ensureSingleExecution,
      async ({ req, res, flash, api }) => {
        const { body, query } = req;
        const { mealPlanId, proxyAthlete } = query;
        const queryParams = `${mealPlanId}/?proxyAthlete=${proxyAthlete}`;

        try {
          await api(`duplicate-day-meal-plan/${queryParams}`, {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.mealPlanUpdatedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
              mealPlannerDate: "",
              mealPlannerMeal: "",
              date: "",
              dayType: "",
              optionId: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .get(
      "/meal-planner/delete-master-food-library/:_id/:type",
      async ({ req, res, flash, api }) => {
        const { params } = req;
        const { _id, type } = params;

        if (
          process.browser &&
          !confirm(
            i18n.t("coaching-portal:mealPlannerActions.confirmDeleteType", {
              type,
            })
          )
        ) {
          return res.backDontScroll();
        }

        const url = type === "food" ? "foods" : "meals";
        try {
          await api(`${url}/${_id}`, {
            method: "DELETE",
          });
          flash(
            "success",
            i18n.t("coaching-portal:mealPlannerActions.successfullyDeleted")
          );

          res.backDontScroll();
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/duplicate-week-meal-plan",
      async ({ req, res, flash, api }) => {
        const { query = {} } = req;
        const { mealPlanId, week } = query;
        try {
          await api(`duplicate-week-meal-plan/${mealPlanId}?week=${week}`, {
            method: "PUT",
          });

          flash(
            "success",
            i18n.t("coaching-portal:mealPlannerActions.successfullyDuplicated")
          );
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/reportion-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("reportion-meal-plan", {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.reportioningStartedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/reportion-meal-plan-status",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        const { status } = body || {};
        try {
          await api("reportion-meal-plan-status", {
            method: "PUT",
            body,
          });
          if (status === "success") {
            flash(
              "success",
              i18n.t(
                "coaching-portal:mealPlannerActions.reportioningCompletedSuccessfully"
              )
            );
          } else if (status === "failed") {
            flash(
              "error",
              i18n.t("coaching-portal:mealPlannerActions.reportioningFailed")
            );
          }
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/reportion-meal-plan-approval",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("reportion-meal-plan-approval", {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.approvalStatusChangedSuccessfully"
            )
          );
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/bulk-reportion-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("bulk-reportion-meal-plan", {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.reportioningStartedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/generate-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("generate-meal-plan", {
            method: "POST",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.generationStartedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "preparing-meal-plan",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/generate-meal-plan-status",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        const { status } = body || {};
        try {
          await api("generate-meal-plan-status", {
            method: "PUT",
            body,
          });
          if (status === "success") {
            flash(
              "success",
              i18n.t(
                "coaching-portal:mealPlannerActions.generationCompletedSuccessfully"
              )
            );
          } else if (status === "failed") {
            flash(
              "error",
              i18n.t("coaching-portal:mealPlannerActions.generationFailed")
            );
          } else if (status === "cancelled") {
            flash(
              "success",
              i18n.t(
                "coaching-portal:mealPlannerActions.generationCancelledSuccessfully"
              )
            );
          }
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/regenerate-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("regenerate-meal-plan", {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.regenerationStartedSuccessfully"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/regenerate-day-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("regenerate-day-meal-plan", {
            method: "PUT",
            body,
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.regenerationStartedSuccessfully"
            )
          );
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/stop-meal-plan-generation",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        const { taskId, mealPlanId } = body || {};
        try {
          await api("stop-meal-plan-generation", {
            method: "POST",
            body: {
              taskId,
              mealPlanId,
            },
          });

          // Update the generation status to "cancelled"
          await api("generate-meal-plan-status", {
            method: "PUT",
            body: {
              mealPlanId,
              status: "cancelled",
            },
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.generationStoppedSuccessfully"
            )
          );
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash(
            "error",
            e.message ||
              i18n.t(
                "coaching-portal:mealPlannerActions.failedToStopGeneration"
              )
          );
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/generate-grocery-list",
      async ({ req, res, flash, api }) => {
        const { query = {} } = req;
        const { mealPlanId, week } = query;
        try {
          await api("generate-grocery-list", {
            method: "POST",
            body: {
              mealPlanId,
              week,
            },
          });

          flash(
            "success",
            i18n.t(
              "coaching-portal:mealPlannerActions.groceryListGeneratedSuccessfully"
            )
          );

          res.refreshWith(
            {
              "modal[type]": "grocery-list",
              "modal[mealPlanId]": mealPlanId,
              "modal[week]": week,
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/reportion-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("reportion-meal-plan", {
            method: "PUT",
            body,
          });

          flash("success", `Meal plan re-portioning started successfully`);
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/reportion-meal-plan-status",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        const { status } = body || {};
        try {
          await api("reportion-meal-plan-status", {
            method: "PUT",
            body,
          });
          if (status === "success") {
            flash("success", "Meal plan re-portioning completed successfully.");
          } else if (status === "failed") {
            flash("error", "Meal plan re-portioning failed.");
          }
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/reportion-meal-plan-approval",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("reportion-meal-plan-approval", {
            method: "PUT",
            body,
          });

          flash("success", `Meal plan approval status changed successfully`);
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/bulk-reportion-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("bulk-reportion-meal-plan", {
            method: "PUT",
            body,
          });

          flash("success", `Meal plan re-portioning started successfully`);
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/generate-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("generate-meal-plan", {
            method: "POST",
            body,
          });

          flash("success", `Meal plan generation started successfully`);
          res.refreshWith(
            {
              "modal[type]": "preparing-meal-plan",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/generate-meal-plan-status",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        const { status } = body || {};
        try {
          await api("generate-meal-plan-status", {
            method: "PUT",
            body,
          });
          if (status === "success") {
            flash("success", "Meal plan generation completed successfully.");
          } else if (status === "failed") {
            flash("error", "Meal plan generation failed.");
          } else if (status === "cancelled") {
            flash("success", "Meal plan generation cancelled successfully.");
          }
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/regenerate-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("regenerate-meal-plan", {
            method: "PUT",
            body,
          });

          flash("success", `Meal plan regeneration started successfully`);
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/regenerate-day-meal-plan",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        try {
          await api("regenerate-day-meal-plan", {
            method: "PUT",
            body,
          });

          flash("success", `Meal plan regeneration started successfully`);
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/stop-meal-plan-generation",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        const { taskId, mealPlanId } = body || {};
        try {
          await api("stop-meal-plan-generation", {
            method: "POST",
            body: {
              taskId,
              mealPlanId,
            },
          });

          // Update the generation status to "cancelled"
          await api("generate-meal-plan-status", {
            method: "PUT",
            body: {
              mealPlanId,
              status: "cancelled",
            },
          });

          flash("success", `Meal plan generation stopped successfully`);
          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message || "Failed to stop meal plan generation");
          res.backDontScroll();
        }
      }
    )
    .post(
      "/meal-planner/generate-grocery-list",
      async ({ req, res, flash, api }) => {
        const { query = {} } = req;
        const { mealPlanId, week } = query;
        try {
          await api("generate-grocery-list", {
            method: "POST",
            body: {
              mealPlanId,
              week,
            },
          });

          flash("success", "Grocery list generated successfully");

          res.refreshWith(
            {
              "modal[type]": "grocery-list",
              "modal[mealPlanId]": mealPlanId,
              "modal[week]": week,
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post("/meal-planner/copy-meal", async ({ req, res, flash }) => {
      const { body } = req;
      const { foods, metadata = {} } = body;
      const now = new Date();
      const timestamp = now.getTime();
      const queryToRefresh = `${foods?.[0]?._id || "meal"}${timestamp}`;

      localStorage.setItem("mealsToCopy", JSON.stringify(foods));
      localStorage.setItem("mealCopyMetadata", JSON.stringify(metadata));

      flash(
        "success",
        i18n.t("coaching-portal:mealPlannerActions.successfullyCopied")
      );
      res.refreshWith({ mealsInClipboard: queryToRefresh }, { url: "back" });
    })
    .post("/meal-planner/paste-meal", async ({ req, res, flash, api }) => {
      const { body, query } = req;
      const {
        targetPlanMode,
        targetMealType,
        // Detailed meal plan parameters
        date,
        // Simplified meal plan parameters
        planMode,
        dayType,
        optionId,
      } = body;

      const { mealPlanId, proxyAthlete, week = 1 } = query;

      // Always read from localStorage at paste time (same as exercises) so paste works across tabs
      const mealsInLocalStorage = localStorage.getItem("mealsToCopy");
      const metadataInLocalStorage = localStorage.getItem("mealCopyMetadata");
      const foodsToPaste = mealsInLocalStorage
        ? JSON.parse(mealsInLocalStorage)
        : [];
      const sourceMeta = metadataInLocalStorage
        ? JSON.parse(metadataInLocalStorage)
        : {};

      if (
        !foodsToPaste ||
        !Array.isArray(foodsToPaste) ||
        foodsToPaste.length === 0
      ) {
        flash(
          "error",
          i18n.t("coaching-portal:mealPlannerActions.noFoodsToPaste")
        );
        res.backDontScroll();
        return;
      }

      // Extract source metadata (only isTemplate is actually used by the endpoint)
      const { isTemplate } = sourceMeta;

      // Determine if target is a template (no athlete means it's a template)
      const isTargetTemplate = !proxyAthlete;

      // Build body for paste-meal-meal-plan API
      let apiBody = {
        foods: foodsToPaste, // Array of foods to paste
        targetMealType: targetMealType,
        isTemplate: isTargetTemplate || isTemplate || false, // Template if no athlete or from source metadata
      };

      if (targetPlanMode === "simplified" || planMode === "simplified") {
        // Simplified meal plan
        apiBody = {
          ...apiBody,
          planMode: "simplified",
          targetDayType: dayType,
          targetOptionId: optionId,
        };
      } else {
        // Detailed meal plan
        // For templates, date comes as "Day X" and needs to be formatted as "Week X Day Y"
        // For real meal plans, date is already a valid date string
        let targetDate = date;
        if (isTargetTemplate && date && !date.match(/^\d{4}-\d{2}-\d{2}/)) {
          // Template: construct "Week X Day Y" format
          // date is like "Day 4", extract the day number
          const dayMatch = date.match(/Day (\d+)/);
          if (dayMatch) {
            const dayNumber = dayMatch[1];
            targetDate = `Week ${week} Day ${dayNumber}`;
          }
        }

        apiBody = {
          ...apiBody,
          targetDate: targetDate, // Use formatted date for detailed mode
        };
      }

      try {
        await api(`paste-meal-meal-plan/${mealPlanId}`, {
          method: "PUT",
          query: proxyAthlete ? { proxyAthlete } : {},
          body: apiBody,
        });
        flash(
          "success",
          i18n.t("coaching-portal:mealPlannerActions.successfullyPasted")
        );
        res.refreshWith({}, { url: "back" });
      } catch (error) {
        flash(
          "error",
          error.message ||
            i18n.t("coaching-portal:mealPlannerActions.failedToPasteMeal")
        );
        res.backDontScroll();
      }
    })
    .post(
      "/meal-planner/meal-plan-review",
      async ({ req, res, flash, api }) => {
        const { body } = req;
        const { mealPlanId, rating, feedback } = body;

        try {
          await api("meal-planner/meal-plan-review", {
            method: "POST",
            body: {
              mealPlanId,
              rating,
              feedback,
            },
          });

          flash(
            "success",
            i18n.t("coaching-portal:mealPlannerActions.thankYouForFeedback")
          );
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (e) {
          flash(
            "error",
            e.message ||
              i18n.t("coaching-portal:mealPlannerActions.failedToSubmitReview")
          );
          res.backDontScroll();
        }
      }
    );
};

export default MealPlanner;
